import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AppliedFilters,
  ChatMessage,
  ChatSession,
  SendMessageResult,
  chatApi,
} from "@/services/chatApi";
import { messagesKey, sessionsKey } from "@/services/chatKeys";
import { startStream, stopStream } from "@/services/chatStreamStore";

export function useChatSessions(projectId: string | undefined) {
  return useQuery({
    queryKey: sessionsKey(projectId ?? ""),
    queryFn: () => chatApi.listSessions(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateChatSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ChatSession, Error, { title?: string }>({
    mutationFn: ({ title }) => chatApi.createSession(projectId, title),
    // Optimistically prepend the new session so the cached list contains it
    // by the time the caller calls setActiveSessionId(...). The auto-select
    // effect in ProjectChat would otherwise see the new id as missing from
    // sessions and reset focus back to whatever was previously first.
    onSuccess: (newSession) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) => [
        newSession,
        ...(prev ?? []).filter((s) => s.id !== newSession.id),
      ]);
    },
  });
}

export function useRenameChatSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ChatSession, Error, { sessionId: string; title: string }>({
    mutationFn: ({ sessionId, title }) => chatApi.renameSession(sessionId, title),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
    },
  });
}

export function useUpdateChatSessionFilters(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    ChatSession,
    Error,
    { sessionId: string; metadataFilters: Record<string, string[]> | null }
  >({
    mutationFn: ({ sessionId, metadataFilters }) =>
      chatApi.updateSessionFilters(sessionId, metadataFilters),
    // Optimistic-style update on success: patch the cached session in place
    // so the UI re-renders with the saved selection without an extra refetch.
    onSuccess: (session) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) =>
        (prev ?? []).map((s) => (s.id === session.id ? session : s)),
      );
    },
  });
}


export function useUpdateChatSessionWebSearch(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ChatSession, Error, { sessionId: string; webSearchEnabled: boolean }>({
    mutationFn: ({ sessionId, webSearchEnabled }) =>
      chatApi.updateSessionWebSearch(sessionId, webSearchEnabled),
    onSuccess: (session) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) =>
        (prev ?? []).map((s) => (s.id === session.id ? session : s)),
      );
    },
  });
}


export function useDeleteChatSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { sessionId: string }>({
    mutationFn: ({ sessionId }) => chatApi.deleteSession(sessionId),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
      qc.removeQueries({ queryKey: messagesKey(sessionId) });
    },
  });
}

export function useSessionMessages(sessionId: string | undefined) {
  return useQuery({
    queryKey: messagesKey(sessionId ?? ""),
    queryFn: async () => {
      // v1: load the first page only; cursor pagination is wired but the UI
      // doesn't yet surface "load older" — assistant turns are appended via
      // mutation, so the cache stays in sync.
      const page = await chatApi.listMessages(sessionId!, { limit: 200 });
      return page.items;
    },
    enabled: !!sessionId,
  });
}

export function useSendChatMessage(sessionId: string, projectId: string, isAdvancedRag?: boolean) {
  const qc = useQueryClient();
  return useMutation<
    SendMessageResult,
    Error,
    { content: string; documentIds?: string[]; }
  >({
    mutationFn: ({ content, documentIds }) =>
      chatApi.sendMessage(sessionId, content, documentIds, undefined, isAdvancedRag),
    onSuccess: ({ userMessage, assistantMessage }) => {
      qc.setQueryData<ChatMessage[]>(messagesKey(sessionId), (prev) => {
        const base = prev ?? [];
        return [...base, userMessage, assistantMessage];
      });
      qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
    },
  });
}

/**
 * Send a message and stream the reply.
 *
 * The stream itself lives in chatStreamStore, outside the React tree, so it
 * survives this component unmounting — a wizard-step change, a session switch,
 * or navigating off the project page no longer kills a half-written answer.
 * Read the live text with useChatStream(sessionId) rather than from here.
 */
export function useStreamChatMessage(sessionId: string, projectId: string, isAdvancedRag?: boolean) {
  const qc = useQueryClient();

  const stream = useCallback(
    (
      content: string,
      documentIds?: string[],
      appliedFilters?: AppliedFilters | null,
    ) =>
      startStream({
        sessionId,
        projectId,
        queryClient: qc,
        content,
        documentIds,
        appliedFilters,
        isAdvancedRag,
      }),
    [sessionId, projectId, qc, isAdvancedRag],
  );

  // Deliberate cancellation only. Nothing calls this on unmount any more —
  // that was the bug.
  const cancel = useCallback(() => stopStream(sessionId), [sessionId]);

  return { stream, cancel };
}
